import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';

const DRAFT_KEY = 'wf_form_draft';

const emptyForm = {
  category: '', name: '', status: 'Available',
  current_company: '', phone: '', email: '', notes: '',
  source: '', qualification: '', experience: '',
  salary: '', preferred_location: '', secondary_location: '', preferred_block: '', client: '', job_role: '', comment: '',
  worked_before: 'No',
  work_history: []
};

export default function WorkerModal({ worker, categories, onClose, onSaved }) {
  const isEdit = !!worker;
  const [form, setForm] = useState(() => {
    if (isEdit) return { ...emptyForm, ...worker, work_history: worker.work_history || [] };
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY));
      return draft || { ...emptyForm, category: categories[0] || '' };
    } catch { return { ...emptyForm, category: categories[0] || '' }; }
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [resumeFile, setResumeFile] = useState(null);
  const [uploadingResume, setUploadingResume] = useState(false);

  // persist draft (only for new workers)
  useEffect(() => {
    if (!isEdit) localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
  }, [form, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addHistory = () => setForm(f => ({
    ...f,
    work_history: [...f.work_history, { company: '', role: '', start_date: '', end_date: '', notes: '' }]
  }));
  const setHistory = (i, k, v) => setForm(f => {
    const wh = [...f.work_history];
    wh[i] = { ...wh[i], [k]: v };
    return { ...f, work_history: wh };
  });
  const removeHistory = i => setForm(f => ({ ...f, work_history: f.work_history.filter((_, j) => j !== i) }));

  const save = async () => {
    if (!form.name.trim()) { setErr('Name is required.'); return; }
    setSaving(true); setErr('');
    try {
      let empId;
      if (isEdit) {
        await axios.put(`/api/employees/${worker.id}`, form);
        empId = worker.id;
      } else {
        const { data } = await axios.post('/api/employees', form);
        empId = data.id;
        localStorage.removeItem(DRAFT_KEY);
      }
      // Upload resume if selected
      if (resumeFile && empId) {
        setUploadingResume(true);
        const fd = new FormData();
        fd.append('resume', resumeFile);
        await axios.post(`/api/employees/${empId}/resume`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      toast.success(isEdit ? '✅ Worker updated successfully!' : '✅ Worker added successfully!');
      onSaved();
    } catch (e) {
      setErr(e.response?.data?.error || 'Save failed');
    } finally { setSaving(false); setUploadingResume(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()} style={{ maxWidth: '720px' }}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Worker' : 'Add New Worker'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {err && <div className="error-msg">{err}</div>}

          <div className="form-row">
            <label>FULL NAME *
              <input value={form.name} onChange={e => set('name', e.target.value)} placeholder="Full name" />
            </label>
            <label>SOURCE
              <input value={form.source || ''} onChange={e => set('source', e.target.value)} placeholder="e.g. Naukri, LinkedIn" />
            </label>
          </div>

          <div className="form-row">
            <label>CONTACT / PHONE
              <input value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="e.g. 9876543210" />
            </label>
            <label>EMAIL
              <input value={form.email} onChange={e => set('email', e.target.value)} placeholder="email@example.com" />
            </label>
          </div>

          <div className="form-row">
            <label>QUALIFICATION
              <input value={form.qualification || ''} onChange={e => set('qualification', e.target.value)} placeholder="e.g. B.Tech, Diploma" />
            </label>
            <label>ALUMNI?
              <select value={form.worked_before || 'No'} onChange={e => set('worked_before', e.target.value)}>
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>EXPERIENCE
              <input value={form.experience || ''} onChange={e => set('experience', e.target.value)} placeholder="e.g. 5 years" />
            </label>
            <label>SALARY
              <input value={form.salary || ''} onChange={e => set('salary', e.target.value)} />
            </label>
          </div>

          <div className="form-row">
            <label>PREFERRED STATE
              <input value={form.preferred_location || ''} onChange={e => set('preferred_location', e.target.value)} placeholder="e.g. Telangana, Tamil Nadu" />
            </label>
            <label>PREFERRED DISTRICT
              <input value={form.secondary_location || ''} onChange={e => set('secondary_location', e.target.value)} placeholder="e.g. Hyderabad, Chennai" />
            </label>
            <label>PREFERRED BLOCK
              <input value={form.preferred_block || ''} onChange={e => set('preferred_block', e.target.value)} placeholder="e.g. Kukatpally, Madipakkam" />
            </label>
          </div>

          <div className="form-row">
            <label>CLIENT
              <input value={form.client || ''} onChange={e => set('client', e.target.value)} placeholder="Client company" />
            </label>
          </div>

          <div className="form-row">
            <label>JOB ROLE
              <select value={form.category} onChange={e => set('category', e.target.value)}>
                <option value="">Select…</option>
                {categories.map(c => <option key={c}>{c}</option>)}
              </select>
            </label>
          </div>

          <div className="form-row">
            <label>STATUS
              <select value={form.status} onChange={e => set('status', e.target.value)}>
                <option>Available</option>
                <option>Occupied</option>
              </select>
            </label>
            {form.status === 'Occupied' && (
              <label>CURRENT COMPANY
                <input value={form.current_company} onChange={e => set('current_company', e.target.value)} placeholder="Company name" />
              </label>
            )}
          </div>

          <label>COMMENT
            <textarea value={form.comment || ''} onChange={e => set('comment', e.target.value)} placeholder="Any comments..." rows={2} />
          </label>

          {/* Resume upload */}
          <div className="resume-upload-section">
            <label style={{ marginBottom: '4px' }}>RESUME</label>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
              <label className="btn-export" style={{ cursor: 'pointer', margin: 0, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                📎 {resumeFile ? resumeFile.name : 'Choose Resume'}
                <input type="file" accept=".pdf,.doc,.docx" onChange={e => setResumeFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
              </label>
              {isEdit && worker.resume_path && !resumeFile && (
                <span style={{ fontSize: '12px', color: 'var(--green)', fontWeight: 600 }}>✓ Resume on file</span>
              )}
              {resumeFile && (
                <button className="btn-danger-sm" style={{ fontSize: '11px' }} onClick={() => setResumeFile(null)}>✕ Remove</button>
              )}
            </div>
          </div>

          <div className="section-header">
            <span>WORK HISTORY</span>
            <button className="btn-outline-sm" onClick={addHistory}>+ Add Entry</button>
          </div>

          {form.work_history.length === 0 && <p className="muted">No history added yet.</p>}
          {form.work_history.map((h, i) => (
            <div key={i} className="history-card">
              <div className="history-card-header">
                <span>Entry {i + 1}</span>
                <button className="btn-danger-sm" onClick={() => removeHistory(i)}>Remove</button>
              </div>
              <div className="form-row">
                <label>Company
                  <input value={h.company} onChange={e => setHistory(i, 'company', e.target.value)} />
                </label>
                <label>Role
                  <input value={h.role} onChange={e => setHistory(i, 'role', e.target.value)} />
                </label>
              </div>
              <div className="form-row">
                <label>Start Date
                  <input type="date" value={h.start_date} onChange={e => setHistory(i, 'start_date', e.target.value)} />
                </label>
                <label>End Date
                  <input type="date" value={h.end_date} onChange={e => setHistory(i, 'end_date', e.target.value)} />
                </label>
              </div>
              <label>Notes
                <input value={h.notes} onChange={e => setHistory(i, 'notes', e.target.value)} />
              </label>
            </div>
          ))}
        </div>

        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving || uploadingResume}>
            {uploadingResume ? 'Uploading Resume…' : saving ? 'Saving…' : isEdit ? 'Update Worker' : 'Add Worker'}
          </button>
        </div>
      </div>
    </div>
  );
}
